import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  FinancialStatementStyles,
  PresetChips,
  amount,
  endOfLastMonth,
  endOfLastYear,
  printStatement,
  today,
} from "./FinancialStatementShared";
import {
  SbStyles,
  SupplierLedger,
  csvCell,
  decode,
  escapeHtml,
} from "./SupplierBalanceReport";

const TITLE = "Ledger Wise Transactions and Present Balance";

const COLUMNS = [
  ["po_value", "PO Value"],
  ["debit", "Debit"],
  ["credit", "Credit"],
  ["mature", "Mature Amount Total"],
  ["balance", "Balance"],
  ["current_mature", "Current Mature"],
];

const isIdle = (row) =>
  !row.entries && Math.abs(num(row.po_value)) < 0.005;

const LedgerWiseTransactionsPresentBalance = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [asOf, setAsOf] = useState(searchParams.get("as_of") || today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState("");
  const [hideIdle, setHideIdle] = useState(false);
  const [open, setOpen] = useState({});
  const [ledgers, setLedgers] = useState({});

  const fetchData = async (date = asOf) => {
    setLoading(true);
    setAlert(null);
    setOpen({});
    setLedgers({});
    setSearchParams({ as_of: date }, { replace: true });

    try {
      const res = await Http.get("/ledger-wise-transactions-present-balance", {
        params: { as_of: date },
      });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the report. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const applyDate = (date) => {
    setAsOf(date);
    fetchData(date);
  };

  // Entries use the payable ledger, whose running balance is credit - debit
  // like this report's.
  const toggleSupplier = (row) => {
    const isOpen = !open[row.id];
    setOpen((values) => ({ ...values, [row.id]: isOpen }));
    if (!isOpen || ledgers[row.id]) return;

    setLedgers((values) => ({ ...values, [row.id]: { loading: true } }));
    Http.get("/supplier-balance-ledger", {
      params: { report: "payable", supplier_id: row.id, as_of: data.as_of },
    })
      .then((res) =>
        setLedgers((values) => ({
          ...values,
          [row.id]: { entries: res.data.entries || [] },
        })),
      )
      .catch((error) =>
        setLedgers((values) => ({
          ...values,
          [row.id]: {
            error:
              error.response?.data?.message || "Could not load the entries.",
          },
        })),
      );
  };

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (data ? data.rows : []).filter(
      (row) =>
        (!hideIdle || !isIdle(row)) &&
        (!query ||
          decode(`${row.code || ""} ${row.name} ${row.contact_person || ""}`)
            .toLowerCase()
            .includes(query)),
    );
  }, [data, search, hideIdle]);

  const totals = useMemo(
    () =>
      COLUMNS.reduce(
        (acc, [key]) => ({
          ...acc,
          [key]: rows.reduce((sum, row) => sum + num(row[key]), 0),
        }),
        {},
      ),
    [rows],
  );

  const dueNow = data
    ? data.rows.filter((row) => num(row.current_mature) > 0.004)
    : [];
  const dueNowTotal = dueNow.reduce((sum, row) => sum + num(row.current_mature), 0);
  const owing = data ? data.rows.filter((row) => num(row.balance) > 0.004) : [];
  const idleCount = data ? data.rows.filter(isIdle).length : 0;

  const scopeLabel =
    data && data.accounts.length
      ? data.accounts.map((a) => `${a.code} - ${a.name}`).join(", ")
      : "all accounts";

  const signedClass = (value) => {
    const n = num(value);
    return n < -0.004 ? "sb-neg" : Math.abs(n) <= 0.004 ? "sb-settled" : "";
  };

  const exportCsv = () => {
    const lines = [
      [TITLE],
      [`As at ${data.as_of}`],
      [],
      ["Sl", "Code", "Supplier", "Contact", ...COLUMNS.map(([, label]) => label)],
      ...rows.map((row, i) => [
        i + 1,
        row.code,
        decode(row.name),
        [row.contact_person, row.contact_phone].filter(Boolean).join(" "),
        ...COLUMNS.map(([key]) => num(row[key]).toFixed(2)),
      ]),
      [],
      ["", "", "Total", "", ...COLUMNS.map(([key]) => totals[key].toFixed(2))],
    ];
    const csv = lines.map((line) => line.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ledger-wise-present-balance_${data.as_of}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const headHtml = `
      <th>Sl</th><th>Code</th><th>Supplier</th>
      ${COLUMNS.map(([, label]) => `<th class="num">${label}</th>`).join("")}`;
    const bodyHtml =
      rows
        .map(
          (row, i) => `
        <tr>
          <td>${i + 1}</td>
          <td class="code">${escapeHtml(row.code)}</td>
          <td>${escapeHtml(decode(row.name))}</td>
          ${COLUMNS.map(([key]) => `<td class="num">${amount(row[key])}</td>`).join("")}
        </tr>`,
        )
        .join("") +
      `<tr class="grand">
        <td colspan="3">Total</td>
        ${COLUMNS.map(([key]) => `<td class="num">${amount(totals[key])}</td>`).join("")}
      </tr>`;

    printStatement({
      title: TITLE,
      subtitle: `As at ${formatDate(data.as_of)} &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note: `Supplier postings on ${scopeLabel} up to the date. Balance = credit - debit; current mature = credit that has fallen due (bill date + mature days) - debit. Amounts in brackets are the other way round.`,
    });
  };

  const stat = (label, icon, value, sub, className = "") => (
    <div className="col-md-3 mg-b-10">
      <div className={`coa-stat ${className}`}>
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{sub}</span>
      </div>
    </div>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <SbStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{TITLE}</h5>
            <p className="text-muted mb-0 tx-12">
              Supplier transactions and balance, as at{" "}
              {formatDate(data?.as_of || asOf)}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={exportCsv}
              disabled={loading || !rows.length}
            >
              <i className="fa fa-file-excel-o mg-r-6"></i>Export CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Statement Date
              </span>
              <span className="coa-head-note">
                Every supplier posting up to the date, on {scopeLabel}
              </span>
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-4">
                  <label>As at</label>
                  <input
                    type="date"
                    className="form-control"
                    value={asOf}
                    onChange={(e) => setAsOf(e.target.value)}
                  />
                  <PresetChips
                    presets={[
                      ["Today", today()],
                      ["Last month end", endOfLastMonth()],
                      ["Last year end", endOfLastYear()],
                    ]}
                    isActive={([, value]) => asOf === value}
                    onPick={([, value]) => applyDate(value)}
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={loading || !asOf}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                "Present Balance",
                "fa-credit-card",
                amount(data.totals.balance),
                `${owing.length} supplier${owing.length === 1 ? "" : "s"} owed`,
              )}
              {stat(
                "Current Mature Due",
                "fa-hourglass-end",
                money(dueNowTotal),
                `${dueNow.length} supplier${dueNow.length === 1 ? "" : "s"} with matured bills unpaid`,
                dueNow.length ? "is-alert" : "is-ok",
              )}
              {stat(
                "PO Value",
                "fa-file-text-o",
                money(data.totals.po_value),
                "Work orders and direct purchases",
              )}
              {stat(
                "Debit / Credit",
                "fa-exchange",
                money(data.totals.debit),
                `Debit · credit ${money(data.totals.credit)}`,
              )}
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>Suppliers
                <span className="coa-head-note mg-l-10">
                  Click a supplier to see its entries
                </span>
              </span>
              {data && data.rows.length > 0 && (
                <span className="sb-tools coa-no-print">
                  <label className="fs-switch">
                    <input
                      type="checkbox"
                      checked={hideIdle}
                      onChange={(e) => setHideIdle(e.target.checked)}
                    />
                    Hide suppliers with no activity ({idleCount})
                  </label>
                  <div className="sb-search">
                    <i className="fa fa-search" aria-hidden="true"></i>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search supplier"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Search supplier"
                    />
                  </div>
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The report could not be loaded.
              </div>
            ) : !rows.length ? (
              <div className="coa-empty">
                <i className="fa fa-check-circle-o"></i>
                {data.rows.length
                  ? "No suppliers match the filters."
                  : "There are no suppliers."}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="sb-table">
                  <thead>
                    <tr>
                      <th>Sl</th>
                      <th>Code</th>
                      <th>Supplier</th>
                      {COLUMNS.map(([key, label]) => (
                        <th key={key} className="coa-num">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {rows.map((row, i) => {
                      const isOpen = !!open[row.id];
                      return (
                        <React.Fragment key={row.id}>
                          <tr className={`sb-row${isOpen ? " is-open" : ""}`}>
                            <td className="sb-sl">{i + 1}</td>
                            <td>
                              {row.code && (
                                <span className="coa-code">{row.code}</span>
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="sb-name-btn"
                                onClick={() => toggleSupplier(row)}
                                aria-expanded={isOpen}
                              >
                                <i
                                  className={`fa fa-caret-${isOpen ? "down" : "right"} mg-r-6`}
                                  aria-hidden="true"
                                ></i>
                                {decode(row.name)}
                              </button>
                              <Link
                                to={`/vendor-details/${row.id}`}
                                className="sb-vendor-link"
                                title="Open the supplier"
                              >
                                <i className="fa fa-external-link"></i>
                              </Link>
                              {row.status === "inactive" && (
                                <span className="fs-inactive">Inactive</span>
                              )}
                              {(row.contact_person || row.contact_phone) && (
                                <span className="sb-sub">
                                  {[row.contact_person, row.contact_phone]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                              )}
                            </td>
                            <td className="coa-num">{amount(row.po_value)}</td>
                            <td className="coa-num">{amount(row.debit)}</td>
                            <td className="coa-num">{amount(row.credit)}</td>
                            <td className="coa-num">{amount(row.mature)}</td>
                            <td
                              className={`coa-num ${signedClass(row.balance)}`}
                              style={{
                                fontWeight: 700,
                                color: num(row.balance) > 0.004 ? "#2a2962" : undefined,
                              }}
                              title={
                                num(row.balance) < -0.004
                                  ? "Overpaid: this is an advance"
                                  : undefined
                              }
                            >
                              {amount(row.balance)}
                            </td>
                            <td
                              className={`coa-num ${signedClass(row.current_mature)}`}
                              style={{ fontWeight: 600 }}
                              title={
                                num(row.current_mature) > 0.004
                                  ? "Matured and not yet paid"
                                  : undefined
                              }
                            >
                              {amount(row.current_mature)}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={9} className="sb-ledger-cell">
                                <SupplierLedger ledger={ledgers[row.id]} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} style={{ textAlign: "right" }}>
                        Total
                      </td>
                      {COLUMNS.map(([key]) => (
                        <td key={key} className="coa-num">
                          {amount(totals[key])}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LedgerWiseTransactionsPresentBalance;
